import { Box, Typography, Stack, Grid, Avatar, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ShareIcon from '@mui/icons-material/Share';
import { Badge } from '../../../../shared/components/ui/Badge';
import { Card } from '../../../../shared/components/ui/Card';
import type { Event } from '../../context/EventContext';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from '@mui/material';
import { useState } from 'react';
import { formatDateToBR } from '../../../../utils/formatDateToBR';
import { formatTime } from '../../../../utils/formatTime';
import { formatBRL } from '../../../../utils/formatBRL';
import { extractYouTubeId } from '../../../../utils/extractYouTubeId';
import { eventService } from '../../../../shared/services/tenant/eventService';
import { useToast } from '../../../../shared/hooks/useToast';


interface EventInfoProps {
  event: Event;
}

export function EventInfo({ event }: EventInfoProps) {
  const [copied, setCopied] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const theme = useTheme();
  const { showToast } = useToast();

  // Usa slug para URL mais curta e amigável
  const eventPath = event.slug ? `/evento/${event.slug}` : `/public/event/${event.id}`;

  // URL do frontend (usa domínio personalizado configurado ou fallback para o atual)
  const frontendUrl = import.meta.env.VITE_FRONTEND_URL || window.location.origin;
  const registrationUrl = `${frontendUrl}${eventPath}`;

  // URL do backend (para compartilhamento - com meta tags OG para WhatsApp/Facebook)
  const backendUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080').replace('/api', '');
  const shareUrl = event.slug ? `${backendUrl}/evento/${event.slug}` : registrationUrl;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePromote = async () => {
    try {
      setIsPromoting(true);
      await eventService.promote(event.id!, "🎉 Novo Evento! Venha participar!");

      showToast('Link de inscrição enviado para o organizador divulgar!', 'success');
    } catch (error: any) {
      showToast('Erro ao enviar link de inscrição', 'error');

    } finally {
      setIsPromoting(false);
    }
  };
  return (
    <>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
        <Box sx={{ position: 'relative', width: 110, height: 110 }}>
          <Avatar src={event?.image || undefined} sx={{ width: 110, height: 110, fontSize: 48, bgcolor: 'primary.main' }}>
            {event.title[0]}
          </Avatar>
        </Box>
        <Box flex={1}>
          <Typography sx={{ color: theme.palette.text.primary, fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>
            {event.title} <Badge color={
              event.status === 'open' ? 'success' :
                event.status === 'closed' ? 'default' : 'info'
            }>
              {event.status === 'open' ? 'Aberto' :
                event.status === 'closed' ? 'Fechado' : 'Cancelado'}
            </Badge>
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 16, fontWeight: 500 }}>
            Organizador: {event?.organizer?.name}
          </Typography>
          <Stack direction="row" spacing={2} mt={1} alignItems="center">
            {event.description && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <BusinessIcon sx={{ color: theme.palette.text.secondary, fontSize: 16, mt: 0.2 }} />
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: 14,
                    '& p': { margin: 0 },
                    '& br': { lineHeight: 1.2 },
                    '& strong': { fontWeight: 600 },
                    '& em': { fontStyle: 'italic' }
                  }}
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </Box>
            )}

          </Stack>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Dados do Evento */}
        <Grid item xs={12}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Dados do Evento</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box component="dl" sx={{ m: 0 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Título</Typography>
                    <Typography variant="body1" color="text.primary" fontWeight={500}>{event.title}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Transporte Próprio</Typography>
                    <Typography variant="body1" color="text.primary">{event.own_transport ? 'Sim' : 'Não'}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Criado em</Typography>
                    <Typography variant="body1" color="text.primary">{new Date(event.created_at ?? '').toLocaleDateString('pt-BR')} às {new Date(event.created_at ?? '').toLocaleTimeString('pt-BR')}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box component="dl" sx={{ m: 0 }}>
                  {event.observation && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Observação</Typography>
                      <Box
                        sx={{
                          '& p': { margin: 0, mb: 1 },
                          '& strong': { fontWeight: 600 },
                          '& em': { fontStyle: 'italic' },
                          fontSize: 14
                        }}
                        dangerouslySetInnerHTML={{ __html: event.observation }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              {event?.video_url && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Vídeo</Typography>
                    <iframe
                      width="100%"
                      height="100%"
                      src={
                        extractYouTubeId(event.video_url)
                          ? `https://www.youtube.com/embed/${extractYouTubeId(event.video_url)}`
                          : undefined
                      }
                      title="Vídeo do evento"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ borderRadius: 12, minHeight: 200, background: '#000' }}
                    />
                  </Box>
                </Grid>
              )}
              {event.description && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Descrição</Typography>
                    <Box
                      sx={{
                        '& p': { margin: 0, mb: 1 },
                        '& strong': { fontWeight: 600 },
                        '& em': { fontStyle: 'italic' },
                        fontSize: 14,
                        p: 2,
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                      dangerouslySetInnerHTML={{ __html: event.description }}
                    />
                  </Box>
                </Grid>
              )}

              {event.rules_for_children && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Regras para Crianças</Typography>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        '& p': { margin: 0, mb: 1 },
                        '& strong': { fontWeight: 600 },
                        '& em': { fontStyle: 'italic' },
                        fontSize: 14
                      }}
                      dangerouslySetInnerHTML={{ __html: event.rules_for_children }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>

        {/* Datas e Ingressos */}
        <Grid item xs={12}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOnIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Datas e Ingressos</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Período do Evento</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Início</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateToBR(event.start_date)}
                    {event.start_time && ` às ${formatTime(event.start_time)}`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Término</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateToBR(event.end_date)}
                    {event.end_time && ` às ${formatTime(event.end_time)}`}
                  </Typography>
                </Box>

                <Box component="dl" sx={{ m: 0 }}>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Data limite inscrição</Typography>
                    <Typography variant="body1" color="text.primary">
                      {event.registration_deadline
                        ? new Date(event.registration_deadline).toLocaleDateString('pt-BR')
                        : '-'}
                    </Typography>
                  </Box>

                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Valores dos Ingressos</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, backgroundColor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.label_ingresso_inteiro ?? 'Inteiro'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary.main">
                        {formatBRL(event.valor_ingresso_inteiro)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, backgroundColor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.label_ingresso_meia ?? 'Meia'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary.main">
                        {formatBRL(event.valor_ingresso_meia)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, backgroundColor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.label_ingresso_estudante ?? 'Estudante'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary.main">
                        {formatBRL(event.valor_ingresso_estudante)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, backgroundColor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.label_ingresso_crianca ?? 'Criança'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary.main">
                        {formatBRL(event.valor_ingresso_crianca)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to={registrationUrl}
              >
                Fazer inscrição
              </Button>
              {/* <Button
                variant="contained"
                color="secondary"
                onClick={handlePromote}
                disabled={isPromoting}
                startIcon={<ShareIcon />}
              >
                {isPromoting ? "Enviando..." : "Enviar para divulgação"}
              </Button> */}
              <Button
                variant="text"
                color={copied ? "success" : "primary"}
                onClick={handleCopy}
              >
                {copied ? "Link copiado!" : "Copiar link"}
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Organizador */}
      <Box sx={{ mt: 4 }}>
        <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
            <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Organizador</Typography>
          </Box>
          {event.organizer ? (
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500 }}>Nome:</dt>
                <dd style={{ margin: 0, marginLeft: 16, color: theme.palette.text.primary }}>{event.organizer.name}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500 }}>Email:</dt>
                <dd style={{ margin: 0, marginLeft: 16, color: theme.palette.text.primary }}>{event.organizer.email}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500 }}>Telefone:</dt>
                <dd style={{ margin: 0, marginLeft: 16, color: theme.palette.text.primary }}>{event.organizer.phone}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500 }}>CPF/RG:</dt>
                <dd style={{ margin: 0, marginLeft: 16, color: theme.palette.text.primary }}>{event.organizer.cpf_or_rg}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500 }}>Nascimento:</dt>
                <dd style={{ margin: 0, marginLeft: 16, color: theme.palette.text.primary }}>{event.organizer.birth_date}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500 }}>Função:</dt>
                <dd style={{ margin: 0, marginLeft: 16, color: theme.palette.text.primary }}>{event.organizer.role}</dd>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ ml: 1 }}>
              Nenhum organizador cadastrado.
            </Typography>
          )}
        </Card>
      </Box>

      {/* Souvenirs */}
      {event.souvenirs && event.souvenirs.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CardGiftcardIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Souvenirs</Typography>
            </Box>
            <Grid container spacing={2}>
              {event.souvenirs.map((souvenir: any) => (
                <Grid item xs={12} md={6} key={souvenir.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img src={souvenir.image} alt={souvenir.title} style={{ width: 60, height: 60, borderRadius: 8 }} />
                    <Box>
                      <Typography fontWeight={700} color={theme.palette.text.primary}>{souvenir.title}</Typography>
                      <Typography color="text.secondary">{souvenir.description}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Box>
      )}
    </>
  );
}